import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Copy } from 'lucide-react';
import { paymentService } from '../../services/paymentService';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import Skeleton from '../../components/common/Skeleton';
import PaymentTimeline from '../../components/payment/PaymentTimeline';
import { formatPrice } from '../../utils/priceCalculator';
import { formatDateTime } from '../../utils/formatRelativeTime';

const ResellerPaymentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, info } = useToast();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await paymentService.getById(id);
      setPayment(data);
    } catch {
      navigate('/revendedor/painel/pagamentos');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-4">
        <Skeleton variant="text" width="33%" />
        <Skeleton variant="block" height={240} />
      </div>
    );
  }

  if (!payment) return null;

  const order = payment.Order || {};
  const paymentUrl = payment.gatewayInvoiceUrl || payment.paymentLink || null;
  const barcode = payment.boletoBarcode || payment.barcodeNumber || null;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      success(`${label} copiado!`);
    } catch {
      info('Use Ctrl+C pra copiar manualmente.');
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/revendedor/painel/pagamentos')}
        className="inline-flex items-center gap-2 text-sm text-app-text-secondary hover:text-app-text transition-colors mb-4"
      >
        <ArrowLeft size={16} /> Voltar para pagamentos
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text mb-1">
            {payment.invoiceNumber || `Cobrança #${payment.id}`}
          </h1>
          <p className="text-sm text-app-text-secondary">
            Criado em {formatDateTime(payment.createdAt)}
          </p>
        </div>
        <StatusBadge domain="payment" status={payment.status} size="md" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Timeline */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-app-text mb-4">Linha do tempo</h2>
            <PaymentTimeline payment={payment} />
          </Card>

          {/* Dados do pagamento */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-app-text mb-4">Detalhes</h2>
            <dl className="divide-y divide-app-border text-sm">
              <div className="flex justify-between py-2">
                <dt className="text-app-text-secondary">Descrição</dt>
                <dd className="font-medium text-app-text text-right">{payment.description || '—'}</dd>
              </div>
              {payment.methodLabel && (
                <div className="flex justify-between py-2">
                  <dt className="text-app-text-secondary">Método</dt>
                  <dd className="font-medium text-app-text">{payment.methodLabel}</dd>
                </div>
              )}
              {payment.orderId && (
                <div className="flex justify-between py-2">
                  <dt className="text-app-text-secondary">Pedido</dt>
                  <dd>
                    <button
                      type="button"
                      onClick={() => navigate(`/revendedor/painel/pedidos/${payment.orderId}`)}
                      className="text-primary hover:underline font-medium"
                    >
                      #{payment.orderId}
                    </button>
                  </dd>
                </div>
              )}
              {payment.dueDate && (
                <div className="flex justify-between py-2">
                  <dt className="text-app-text-secondary">Vencimento</dt>
                  <dd className="font-medium">{formatDateTime(payment.dueDate)}</dd>
                </div>
              )}
              {payment.paidAt && (
                <div className="flex justify-between py-2 text-success">
                  <dt>Pago em</dt>
                  <dd className="font-medium">{formatDateTime(payment.paidAt)}</dd>
                </div>
              )}
              {payment.transactionId && (
                <div className="flex justify-between py-2">
                  <dt className="text-app-text-secondary">Transação</dt>
                  <dd className="font-mono text-xs text-app-text">{payment.transactionId}</dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Boleto / Link de pagamento */}
          {payment.status === 'pending' && (paymentUrl || barcode) && (
            <Card className="p-6 bg-info/5 border-info/30">
              <h2 className="text-lg font-bold text-app-text mb-3">Como pagar</h2>
              {paymentUrl && (
                <a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="block mb-3">
                  <Button fullWidth leftIcon={<ExternalLink size={16} />}>
                    Abrir página de pagamento
                  </Button>
                </a>
              )}
              {barcode && (
                <div>
                  <p className="text-xs font-semibold text-app-text-secondary uppercase mb-1">
                    Linha digitável
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-xs text-app-text bg-white px-3 py-2 rounded border border-app-border flex-1 truncate">
                      {barcode}
                    </code>
                    <Button
                      size="sm"
                      variant="subtle"
                      onClick={() => copy(barcode, 'Código')}
                      leftIcon={<Copy size={14} />}
                    >
                      Copiar
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Itens do pedido relacionado */}
          {Array.isArray(order.items) && order.items.length > 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-bold text-app-text mb-4">Itens do pedido</h2>
              <ul className="divide-y divide-app-border">
                {order.items.map((item) => (
                  <li key={item.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-app-text">{item.productName}</p>
                      {item.variationLabel && (
                        <p className="text-xs text-app-text-secondary">{item.variationLabel}</p>
                      )}
                      <p className="text-xs text-app-text-secondary">
                        {item.quantity} × {formatPrice(item.unitPrice)}
                      </p>
                    </div>
                    <p className="font-semibold text-app-text">
                      {formatPrice(item.unitPrice * item.quantity)}
                    </p>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <div>
          <Card className="p-6 lg:sticky lg:top-20">
            <h2 className="text-lg font-bold text-app-text mb-4">Resumo</h2>
            <dl className="space-y-2 text-sm mb-4">
              {order.subtotal > 0 && (
                <div className="flex justify-between">
                  <dt className="text-app-text-secondary">Subtotal</dt>
                  <dd>{formatPrice(parseFloat(order.subtotal))}</dd>
                </div>
              )}
              {order.totalCommission > 0 && (
                <div className="flex justify-between text-success">
                  <dt>Comissão ganha</dt>
                  <dd className="font-semibold">+{formatPrice(parseFloat(order.totalCommission))}</dd>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-app-border font-bold text-lg">
                <dt>Valor</dt>
                <dd className="text-primary">{formatPrice(parseFloat(payment.amount))}</dd>
              </div>
            </dl>
            {payment.status === 'paid' && (
              <div className="p-3 bg-success/5 border border-success/30 rounded-lg text-sm text-success">
                Pagamento confirmado em {formatDateTime(payment.paidAt)}.
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResellerPaymentDetail;
